import type { BriefView } from '@ni/shared';
import type { BriefViewRequest } from '../../api/client';
import { formatDay } from '../../lib/format';

/**
 * Which dossier the research page is showing, and the way across (§3e option b,
 * decided 2026-08-31).
 *
 * A dossier that rewrote itself under the reader mid-run did not read as a
 * record, so while a run is live the brief is pinned to the last finished one.
 * That pin is only honest if the page says it is pinned — otherwise the office
 * reads yesterday's dossier believing it is today's, which is the worse of the
 * two failures. So: one quiet line saying which dossier this is, and one link
 * to the other.
 *
 * It mounts under the run strip because it is a fact about the live run, not
 * about the brief. With no run live the server answers `current`, this renders
 * nothing, and the page is what it was before any of this existed.
 */
export function BriefViewNote({
  view,
  asOfCompletedAt,
  canPin,
  onView,
}: {
  view: BriefView;
  /** The last finished research run's completion, for the "as of" clause. */
  asOfCompletedAt: string | null;
  /**
   * Whether a finished dossier exists to pin to. A first run has none, so its
   * draft is simply the page — no toggle, and the lede says it is in progress
   * instead (research.tsx). Derived from the run rows by the same rule the
   * server pins by, rather than from which view was requested, so a view that
   * has not caught up with the request yet cannot flash the wrong control.
   */
  canPin: boolean;
  onView: (view: BriefViewRequest) => void;
}) {
  // No run live: nothing is pinned, nothing to explain, nothing to offer.
  if (view === 'current') return null;
  // The server chose the draft because there was nothing else to serve.
  if (view === 'draft' && !canPin) return null;

  const stable = view === 'stable';
  const asOf = stable ? formatDay(asOfCompletedAt) : '';

  return (
    <p className="src brief-view">
      {stable
        ? `Showing the last finished brief${asOf ? `, as of ${asOf}` : ''}. A new run is underway.`
        : 'Live draft. Sections update as research runs.'}{' '}
      <button
        type="button"
        className="linkish flush"
        onClick={() => onView(stable ? 'draft' : 'auto')}
      >
        {stable ? 'Show the live draft' : 'Back to the last finished brief'}
      </button>
    </p>
  );
}

/**
 * The first run's own label. There is no finished dossier behind it, so there
 * is no choice to offer — only the plain fact that what is on the page is still
 * being written. Mounts in the lede area, beside the summary rather than inside
 * it, the way caveats do (commit 026c29f).
 *
 * **`moving` is what makes the sentence true.** The view alone is not enough:
 * the server answers `draft` for every non-terminal run, so keying on it printed
 * "Run in progress — this page updates as findings land" over a run that was
 * parked on a question and would update nothing until the office answered it.
 * Two lines then said opposite things about the same run — the strip's "More
 * detail needed" right above this one. So the note follows the run rather than
 * the view: shown while the pipeline owns the run or its continuation is
 * enqueued (`isRunMoving`), silent through a wait, where the strip is the line
 * that speaks.
 */
export function DraftInProgressNote({
  view,
  canPin,
  moving,
}: {
  view: BriefView;
  canPin: boolean;
  /** `isRunMoving` on the polled run: working, or in the resuming window. */
  moving: boolean;
}) {
  if (view !== 'draft' || canPin) return null;
  if (!moving) return null;
  return <p className="topline">Research in progress.</p>;
}
